import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { ApplicationStep, PublicClassNames, ReturnToSearchText, StepbystepApplicationStep } from 'embed/constants'

import AdvertiserDisclosure from 'embed/components/AdvertiserDisclosure'
import { BackButton } from 'embed/components/Application/BackButton'
import { EmailDisclaimer } from 'embed/components/Application/EmailDisclaimer'
import { Progress } from 'embed/components/Application/Progress'
import { ProgressBar } from 'embed/components/Application/ProgressBar'
import { ReturnToSearch } from 'embed/components/Application/ReturnToSearch'
import { TCPALanguage } from 'embed/components/Application/TCPALanguage'
import ApplicationSubmitButton from 'embed/components/ApplicationSubmitButton'
import ApplicationSubmitButtonContainer from 'embed/components/ApplicationSubmitButton/container'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { CreditCardStep, Product } from 'even-ts-static'

const applicationStyles = require('embed/styles/application')
const styles = require('./styles')

interface Props {
  children: React.ReactNode
  directReferral?: boolean
  dispatchReturnToOriginalStep: () => void
  getOffersCta?: boolean
  handleConsentsToFCRAandTCPA?: () => void
  onBack: () => void
  onContinue: () => void
  shouldDisplayBackToSearch?: boolean
  stepByStepApplicationStep: ApplicationStep | undefined
  stepByStepApplicationStepIsValid?: boolean
  currentStep: number
  totalNumberOfSteps: number
}

const BUTTON_TEXT = 'Agree and Continue'

export const StepbystepCreditApplicationWrapper: React.FC<Props> = (props) => {
  const headerLink: () => React.ReactNode = () => {
    if (props.currentStep === 1) {
      if (props.shouldDisplayBackToSearch && !props.directReferral) {
        return (
          <ReturnToSearch
            className={applicationStyles.backToSearch}
            onClick={props.dispatchReturnToOriginalStep}
            text={ReturnToSearchText.Back}
          />
        )
      }
      return null
    }
    return <BackButton className={applicationStyles.backToSearch} onClick={props.onBack} />
  }

  let ctaButton: React.ReactNode
  const isLastStep = props.currentStep === props.totalNumberOfSteps
  if (props.getOffersCta || isLastStep) {
    ctaButton = (
      <>
        <ApplicationSubmitButtonContainer
          product={Product.CreditCard}
          nextStep={CreditCardStep.Results}
          handleBeforeSubmit={props.handleConsentsToFCRAandTCPA}
        >
          <span className={applicationStyles.consentButtonLabel}>{BUTTON_TEXT}</span>
        </ApplicationSubmitButtonContainer>
        <TCPALanguage
          buttonText={BUTTON_TEXT}
          className={applicationStyles.consentWithButton}
          product={Product.CreditCard}
        />
      </>
    )
  } else {
    ctaButton = (
      <ApplicationSubmitButton
        fullWidth={true}
        isValid={!!props.stepByStepApplicationStepIsValid}
        onSubmit={props.onContinue}
      >
        Continue
      </ApplicationSubmitButton>
    )
  }

  return (
    <ThemeConsumer>
      {({ backdropStyles, borderRadiusLarge, boxStyles, colorBorder }) => (
        <React.Fragment>
          <div
            className={classnames(
              PublicClassNames.ApplicationContainer,
              applicationStyles.container,
              styles.application,
              css`
                background-color: ${backdropStyles.backgroundColor};
                border-color: ${colorBorder};
                border-radius: ${borderRadiusLarge};
              `,
            )}
            style={boxStyles}
          >
            <div className={classnames(styles.applicationHeader)}>
              <Progress currentStep={props.currentStep} totalNumberOfSteps={props.totalNumberOfSteps} />
            </div>
            <div className={classnames(applicationStyles.applicationHeader, styles.applicationHeader)}>
              {headerLink()}
              <div className={applicationStyles.advertiserDisclosure}>
                <AdvertiserDisclosure />
              </div>
            </div>
            <ProgressBar
              totalNumberOfSteps={props.totalNumberOfSteps}
              currentStep={props.currentStep}
              className={styles.progressBar}
            />
            <section className={applicationStyles.formSection}>
              <div className={applicationStyles.centeredField}>{props.children}</div>
            </section>
            {ctaButton}
            {props.stepByStepApplicationStep === StepbystepApplicationStep.Email && (
              <section className={applicationStyles.formSection}>
                <div className={classnames(styles.disclaimerContainer)}>
                  <EmailDisclaimer />
                </div>
              </section>
            )}
          </div>
        </React.Fragment>
      )}
    </ThemeConsumer>
  )
}
